import { evaluateTemplateApplicability, filterTemplatesByRegion } from "./regionFilter.js";

export function hasCompanyScope(companyRegion) {
  return Boolean(companyRegion?.shortName || companyRegion?.industrySource || companyRegion?.scopeRequired);
}

function countVisibleFilesBySubmodule(files, companyRegion) {
  const counts = new Map();
  // 先在文件层完成地区/行业过滤，再把结果回填到子模块，避免空分类继续出现在左侧目录。
  const scopedFiles = filterTemplatesByRegion(
    files.map((file) => ({ ...file, kind: file.kind || "system" })),
    companyRegion,
  );
  for (const file of scopedFiles) {
    if (!file.submoduleKey) continue;
    counts.set(file.submoduleKey, (counts.get(file.submoduleKey) || 0) + 1);
  }
  return counts;
}

function cleanLabel(value) {
  return String(value || "").trim();
}

export function resolveTemplateSubmodule(template, moduleData, fallbackSubmoduleKey = "") {
  const submodules = moduleData?.submodules || [];
  const explicitKey = cleanLabel(template?.submoduleKey || fallbackSubmoduleKey);
  if (explicitKey) {
    const matchedByKey = submodules.find((submodule) => submodule.submoduleKey === explicitKey);
    if (matchedByKey) return matchedByKey;
  }

  const candidateNames = [
    template?.submoduleName,
    template?.scenarioName,
    template?.currentProject,
  ].map(cleanLabel).filter(Boolean);
  for (const candidateName of candidateNames) {
    const matchedByName = submodules.find(
      (submodule) => cleanLabel(submodule.submoduleName) === candidateName,
    );
    if (matchedByName) return matchedByName;
  }

  return null;
}

export function withTemplateSubmodule(template, moduleData, fallbackSubmoduleKey = "") {
  const submodule = resolveTemplateSubmodule(template, moduleData, fallbackSubmoduleKey);
  return {
    ...template,
    moduleKey: template?.moduleKey || moduleData?.moduleKey || "",
    moduleName: template?.moduleName || moduleData?.moduleName || "",
    submoduleKey: template?.submoduleKey || submodule?.submoduleKey || "",
    submoduleName: template?.submoduleName || submodule?.submoduleName || template?.scenarioName || "",
  };
}

export function submodulesWithVisibleCounts(moduleData, companyRegion, extraFiles = []) {
  const submodules = moduleData?.submodules || [];
  if (!Array.isArray(moduleData?.files)) {
    const extraCounts = countVisibleFilesBySubmodule(extraFiles, companyRegion);
    // 兼容只有子模块摘要、没有文件明细的旧数据包：用子模块名称做一次粗粒度适用性判断。
    const summarized = submodules
      .map((submodule) => ({
        ...submodule,
        visibleFileCount: (submodule.fileCount || 0) + (extraCounts.get(submodule.submoduleKey) || 0),
      }));
    return (hasCompanyScope(companyRegion) ? summarized.filter((submodule) =>
        evaluateTemplateApplicability(
          {
            fileName: submodule.submoduleName,
            displayName: submodule.submoduleName,
            submoduleName: submodule.submoduleName,
            scenarioName: moduleData?.moduleName,
          },
          companyRegion,
        ).visible,
      ) : summarized)
      .filter((submodule) => submodule.visibleFileCount > 0);
  }

  // 有文件明细时始终按同一套可见性规则计数，保证侧栏数字与右侧实际列表一致。
  const counts = countVisibleFilesBySubmodule(moduleData.files.concat(extraFiles), companyRegion);
  return submodules
    .map((submodule) => ({
      ...submodule,
      visibleFileCount: counts.get(submodule.submoduleKey) || 0,
    }))
    .filter((submodule) => submodule.visibleFileCount > 0);
}
